import { cn } from '@/lib/utils.ts';
import { Button } from '@/components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx';
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from '@/components/ui/field.tsx';
import { Input } from '@/components/ui/input.tsx';
import { useLogin, useTranslate } from 'ra-core';
import React, { useState } from 'react';
import { useErrorNotifier } from '@/hooks/useErrorNotifier.tsx';
import { useMutation } from '@tanstack/react-query';
import { EmailVerificationSent } from '@/customRoutes/authentication/components/EmailVerificationSent.tsx';
import { Link } from 'react-router-dom';

type Props = { type: 'login' | 'signup' } & React.ComponentProps<'div'>;

export function LoginForm({ className, type, ...props }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const t = useTranslate();
  const notifyError = useErrorNotifier({ prefix: 'auth.error' });
  const login = useLogin();
  const { mutate: loginEmailAndPassword, isPending } = useMutation({
    mutationFn: () => {
      return login({
        email,
        password,
        provider: 'emailAndPassword',
        isSignup: type === 'signup',
      });
    },
    onError: (error: any) => {
      console.error(error);
      if (error?.hasToVerifyEmail) {
        setVerificationEmailSent(true);
      } else {
        notifyError(error);
      }
    },
  });

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    loginEmailAndPassword();
  };

  const handleGoogleClick = async () => {
    try {
      // Sign up and login is the same when using google provider
      await login({
        provider: 'google',
        isSignup: type === 'signup',
      });
    } catch (error: any) {
      console.error('google signup failed', error);
      notifyError(error);
    }
  };

  if (verificationEmailSent) {
    return <EmailVerificationSent email={email} />;
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-6 animate-in fade-in-0 zoom-in-95 duration-500',
        className
      )}
      {...props}
    >
      <div>
        <Link to="/">
          <img
            alt="logo"
            src="https://storage.googleapis.com/public-goloot-production/goloot/goloot-logo-white.png"
            style={{ height: 55, width: 'auto', margin: 'auto' }}
          />
        </Link>
      </div>
      <Card className="shadow-elev-3">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">
            {type === 'login' ? 'Welcome back' : 'Create an account'}
          </CardTitle>
          <CardDescription>
            {type === 'login'
              ? `Login with your Google account`
              : `Sign up with your Google account`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleFormSubmit}>
            <FieldGroup>
              <Field>
                <Button
                  variant="outline"
                  type="button"
                  onClick={handleGoogleClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path
                      d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                      fill="currentColor"
                    />
                  </svg>
                  {type === 'login'
                    ? t('auth.button.login_google')
                    : t('auth.button.signup_google')}
                </Button>
              </Field>
              <FieldSeparator className="*:data-[slot=field-separator-content]:bg-card">
                Or continue with
              </FieldSeparator>
              <Field>
                <FieldLabel htmlFor="email">{t('auth.label.email')}</FieldLabel>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="username email"
                  placeholder="m@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </Field>
              <Field>
                <div className="flex items-center">
                  <FieldLabel htmlFor="password">
                    {t('ra.auth.password')}
                  </FieldLabel>
                  {type === 'login' && (
                    <Link
                      to="/forgot-password"
                      className="ml-auto text-sm underline-offset-4 hover:underline"
                    >
                      {t('auth.message.reset_password.forgot_password')}
                    </Link>
                  )}
                </div>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
              </Field>
              <Field>
                <Button type="submit" disabled={isPending}>
                  {type === 'login'
                    ? t('auth.button.login')
                    : t('auth.button.signup')}
                </Button>
                {type === 'signup' && (
                  <FieldDescription
                    className="text-center"
                    dangerouslySetInnerHTML={{
                      __html: t('auth.message.signup.footer'),
                    }}
                  />
                )}
              </Field>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
      <FieldDescription className="px-6 text-center">
        By clicking continue, you agree to our{' '}
        <a href="https://www.goloot.io/eng/terms-and-conditions">
          Terms of Service
        </a>{' '}
        and{' '}
        <a href="https://www.goloot.io/eng/privacy-policy">Privacy Policy</a>.
      </FieldDescription>
    </div>
  );
}
